import React, { useState } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CommonContainer from '../../../common/CommonContainer';
import { clearStandardReports } from '../../../../actions/report-actions';
import Sie4ExportForm from './Sie4ExportForm';
import {
  BACKGROUND_DARK,
  BACKGROUND_WHITE, DASHBOARD_BOX_SHADOW, DASHBOARD_BOX_SHADOW_DARK, LIGHT_GREY_COLOR,
  WHITE_COLOR,
} from '../../../../const/color-const';
import NoDataAvailableWithIcon from '../../../common/charts/NoDataAvailableWithIcon';
import { darken } from '@material-ui/core/styles';
import { formatTimestampAsDateWithTime } from '../../../../utils/dates-utils';
import { JANUARY } from '../../../../utils/dates-const';

const useStyles = makeStyles(theme => ({
  pageWrapper: {
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    marginTop: theme.spacing(2),
    background: theme.palette.type === 'light' ? BACKGROUND_WHITE : BACKGROUND_DARK,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
  },
  topSectionWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: `calc(100vw - 300px)`,
    },
  },
  tableWrapper: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(4),
  },
  dotsSection: {
    fontSize: 36,
    display: 'flex',
    userSelect: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    color: LIGHT_GREY_COLOR,
    margin: theme.spacing(2, 2, 4, 2),
  },
  singleReportWrapper: {
    borderRadius: '12px',
    boxShadow: theme.palette.type === 'light' ? DASHBOARD_BOX_SHADOW : DASHBOARD_BOX_SHADOW_DARK,
  },
  divider: {
    textAlign: 'center',
  },
  reportTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  headerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.type === 'light' ? WHITE_COLOR : darken(BACKGROUND_DARK, 0.2),
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    '& div': {
      flex: '1 1 30%',
      textAlign: 'left',
    },
    '& div:nth-child(2)': {
      textAlign: 'center',
    },
    '& div:nth-child(3)': {
      textAlign: 'right',
    },
  },
  accountsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    overflowX: 'auto',
  },
  accountTable: {
    flex: '1 1 0',
    minWidth: '150px',
    maxWidth: '300px',
    backgroundColor: theme.palette.type === 'light' ? WHITE_COLOR : darken(BACKGROUND_DARK, 0.2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  tableHeader: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  topSalesHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    '& button': {
      marginLeft: 0,
      marginRight: 0,
    },
  },
}));

const Sie4ReportExport = ({
                            machines,
                            reportDetails,
                            reports,
                            clearStandardReports,
                          }) => {
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const [formValues, setFormValues] = useState({
    machineIDs: machinesOptions.length > 0 ? machinesOptions[0].value : '',
    dateFrom: JANUARY.option.dateFrom,
    dateTo: JANUARY.option.dateTo,
  });
  const { reportName, reportTitle } = reportDetails;
  const classes = useStyles();
  
  const getMachineName = (machineID, machinesOptions) => {
    return machinesOptions.find(machine => machine.value === machineID)?.label || 'machine';
  };
  
  const machineName = formValues?.machineIDs ? getMachineName(formValues.machineIDs, machinesOptions) : '';
  
  React.useEffect(() => {
    clearStandardReports();
  }, [clearStandardReports, formValues, reportDetails]);
  
  const renderHeader = report => (
    <div className={classes.headerWrapper}>
      <div className={classes.headerRow}>
        <div><strong>Z Report:</strong> {report.ZReport}</div>
        <div><strong>First Transaction:</strong> {formatTimestampAsDateWithTime(report.FirstTransactionDateTime)}</div>
        <div><strong>Last Transaction:</strong> {formatTimestampAsDateWithTime(report.LastTransactionDateTime)}</div>
      </div>
    </div>
  );
  
  const renderAccountTables = accounts => (
    <div className={classes.accountsWrapper}>
      {accounts?.map((account, index) => (
        <div key={index} className={classes.accountTable}>
          <div className={classes.tableHeader}>{account.Number} ({account.Description || 'N/A'})</div>
          <div>
            <strong>{account.Amount} kr</strong>
          </div>
        </div>
      ))}
    </div>
  );
  
  return (
    <CommonContainer hAlign="flex-start" className={classes.pageWrapper}>
      <div className={classes.topSectionWrapper}>
        <h1 className={classes.topSalesHeader}>{reportTitle}</h1>
        <Sie4ExportForm
          reportName={reportName}
          machinesOptions={machinesOptions}
          setFormValues={setFormValues}
          machineName={machineName}
        />
      </div>
      
      
      {reports && (
        <div className={classes.tableWrapper}>
          {reports?.length > 0 && reports.map((report, index) => (
            <>
              <div className={classes.singleReportWrapper} key={index}>
                {renderHeader(report)}
                {renderAccountTables(report.Accounts)}
              </div>
              {index !== reports.length - 1 && <span className={classes.dotsSection}>...</span>}
            </>
          ))}
        </div>
      )}
      
      {!!reports && reports.length === 0 && <NoDataAvailableWithIcon />}


    </CommonContainer>
  );
};

Sie4ReportExport.propTypes = {};

const decorate = compose(
  connect(
    store => ({
      machines: store.user.machines,
      reports: store.report.standard,
    }),
    dispatch => bindActionCreators({ clearStandardReports }, dispatch))
);

export default decorate(Sie4ReportExport);
